import { agent } from '@shared/services/agent';

export async function getAllWhereaboutsOptions(): Promise<WhereaboutsOption[]> {
  return (await agent.requests.get('/api/whereabouts-options/all'));
}

export async function deleteWhereaboutsOption(option: WhereaboutsOption): Promise<void> {
  return (await agent.requests.del(`/api/whereabouts-options/${option.id}`, true));
}

export async function createUpdateWhereaboutsOption(request: WhereaboutsOption): Promise<void> {
  return (await agent.requests.post('/api/whereabouts-options', request, true));
}

export async function getRecentDayNotes(): Promise<RecentDayNotes[]> {
  return (await agent.requests.get('/api/whereabouts-options/recent-day-notes'));
}

export interface WhereaboutsOptionItem {
  // Undefined for newly added items; set when editing an existing item
  id?: number;
  label: string;
}

export interface WhereaboutsOption {
  id: number;

  dataTest: string;
  alt: string;
  image: string;

  key: any;
  companyId: string;
  status: string;
  label: string;
  outlookNote: string;
  freeBusyStatus: FreeBusyStatus;
  slackNote: string;

  pickable: boolean;
  freeTextEnabled: boolean;
  required: boolean;
  enabled: boolean;

  colour: string;

  requiresApproval: boolean;
  whereaboutsType: CustomWhereaboutsType;
  isPrivate: boolean;
  dayNotesEnabled: boolean;
  schengen?: boolean;
  optionListEnabled: boolean;
  optionListFreeTextEnabled: boolean;
  optionItems?: WhereaboutsOptionItem[];
}

export enum CustomWhereaboutsType {
  CUSTOM = "CUSTOM",
  LEAVE = "LEAVE",
}

export enum FixedWhereabouts {
  HomeRejected = 'HOME_REJECT',
  HomeApproved = 'HOME_APPROVED',
  Home = 'HOME',
  HomePending = 'HOME_PENDING',
  Office = 'OFFICE',
  HolidayPending = 'HOLIDAY_PENDING',
  HolidayRejected = 'HOLIDAY_REJECTED',
  HolidayApproved = 'HOLIDAY_APPROVED',
  Holiday = 'HOLIDAY',
  NotWorking = 'NOT_WORKING',
  Unknown = 'UNKNOWN',
  CompanyHoliday = 'COMPANY_HOLIDAY'
}

const HOME_ID = 1;
const OFFICE_ID = 2;
export const UNKNOWN_ID = 3;
export const COMPANY_HOLIDAY_ID = 4;
export const HOLIDAY_ID = 5;
const HOLIDAY_APPROVED_ID = 6;
const HOLIDAY_REJECTED_ID = 7;
const HOLIDAY_PENDING_ID = 8;
export const NON_WORKING_DAY = 9;

export function getDefaultWhereaboutsKeyFromId(id: number) {
  if (id === HOME_ID) return FixedWhereabouts.Home;
  else if (id === OFFICE_ID) return FixedWhereabouts.Office;
  else if (id === UNKNOWN_ID) return FixedWhereabouts.Unknown;
  else if (id === COMPANY_HOLIDAY_ID) return FixedWhereabouts.CompanyHoliday;
  else if (id === HOLIDAY_ID) return FixedWhereabouts.Holiday;
  else if (id === HOLIDAY_APPROVED_ID) return FixedWhereabouts.HolidayApproved;
  else if (id === HOLIDAY_REJECTED_ID) return FixedWhereabouts.HolidayRejected;
  else if (id === HOLIDAY_PENDING_ID) return FixedWhereabouts.HolidayPending;
  else if (id === NON_WORKING_DAY) return FixedWhereabouts.NotWorking;
}

export const FixedWhereaboutsValues = Object.keys(FixedWhereabouts);

export enum FreeBusyStatus {
  FREE = 'Free',
  BUSY = 'Busy',
  OOF = 'Oof',
  TENTATIVE = 'Tentative',
  WORKING_ELSEWHERE = 'WorkingElsewhere',
}

export const UNKNOWN_OPTION: WhereaboutsOption = {
  id: 3,
  dataTest: 'unknown-option',
  alt: 'Unknown',
  image: '',
  key: FixedWhereabouts.Unknown,
  companyId: '',
  status: 'Unknown',
  label: 'Unknown',
  outlookNote: '',
  freeBusyStatus: FreeBusyStatus.FREE,
  slackNote: '',
  pickable: false,
  freeTextEnabled: false,
  required: false,
  enabled: false,
  colour: 'grey',
  requiresApproval: false,
  whereaboutsType: CustomWhereaboutsType.CUSTOM,
  isPrivate: false,
  dayNotesEnabled: false,
  optionListEnabled: false,
  optionListFreeTextEnabled: false,
}

export const OFFICE_OPTION: WhereaboutsOption = {
  id: 2,
  dataTest: 'office-option',
  alt: 'Office',
  image: 'Office.svg',
  key: 'OFFICE',
  companyId: '',
  status: 'OFFICE',
  label: 'Office',
  outlookNote: '',
  freeBusyStatus: FreeBusyStatus.FREE,
  slackNote: '',
  pickable: false,
  freeTextEnabled: false,
  required: false,
  enabled: false,
  colour: 'green',
  requiresApproval: false,
  whereaboutsType: CustomWhereaboutsType.CUSTOM,
  isPrivate: false,
  dayNotesEnabled: false,
  optionListEnabled: false,
  optionListFreeTextEnabled: false,
}

export interface RecentDayNotes {
  note: string;
  whereaboutsOptionId: number;
}
